import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import TextArea from "../../Shared/TextArea";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import TextAreaFormSkeleton from "../../Shared/LoadingSkeletons/TextAreaFormSkeleton";
import {
  useBookingPage,
  useUpdatePoliciesTab,
} from "../../../hooks/useBookingPage";

const PaymentTermsForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const maxCharacters = 1000;

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Update mutation for policies tab
  const { mutate: updatePolicies, isPending: isUpdating } =
    useUpdatePoliciesTab();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      paymentTerms: "",
    },
  });

  // Watch form values for character count
  const paymentTerms = watch("paymentTerms");

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const formData = {
        paymentTerms: configData.data.paymentTerms || "",
      };
      reset(formData, { keepDirty: false });
      setCharCount(configData.data.paymentTerms?.length || 0);
    }
  }, [configData, reset]);

  // Update character count
  useEffect(() => {
    setCharCount(paymentTerms?.length || 0);
  }, [paymentTerms]);

  const onSubmit = async (data) => {
    setErrors({});
    setSuccess(false);

    const formData = {
      paymentTerms: data.paymentTerms || null,
    };

    updatePolicies(formData, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage = errorMessage || "Failed to update payment terms";

        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Update Failed",
          message: displayMessage,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <TextAreaFormSkeleton />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="px-4 py-6 sm:p-8">
        <div className="space-y-6">
          <TextArea
            label="Payment Terms"
            name="paymentTerms"
            helperText="Optional — leave blank to skip. Clients will see this on your booking page if set."
            placeholder="e.g., Payment is due at the time of service. We accept cash, credit cards, and digital payments. A 50% deposit is required for appointments over $100."
            charCount={charCount}
            maxLength={maxCharacters}
            {...register("paymentTerms")}
          />
        </div>
      </div>

      <FormFooter
        primaryCaption="Save Changes"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Payment terms updated successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default PaymentTermsForm;
